import { useState, useEffect } from 'react'
import type CrmFundraisingOpportunity from '@/types/serializers/CrmFundraisingOpportunity'
import type CrmFundraisingDonation from '@/types/serializers/CrmFundraisingDonation'
import type CrmFundraisingRecurringDonation from '@/types/serializers/CrmFundraisingRecurringDonation'
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { Skeleton } from '@/components/ui/skeleton'
import OpportunityShow from '../Show'

interface ShowProps {
  opportunity: CrmFundraisingOpportunity
  donations: CrmFundraisingDonation[]
  recurring_donations: CrmFundraisingRecurringDonation[]
}

interface OpportunityDetailSheetProps {
  opportunityId: number | null
  onOpenChange: (open: boolean) => void
}

export function OpportunityDetailSheet({ opportunityId, onOpenChange }: OpportunityDetailSheetProps) {
  const [data, setData] = useState<ShowProps | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!opportunityId) {
      setData(null)
      return
    }
    setLoading(true)

    const controller = new AbortController()
    fetch(`/fundraising/opportunities/${opportunityId}`, {
      headers: { 'Accept': 'application/json' },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load')
        return res.json()
      })
      .then((json) => setData(json))
      .catch((err) => {
        if (err.name !== 'AbortError') console.error(err)
      })
      .finally(() => setLoading(false))

    return () => controller.abort()
  }, [opportunityId])

  return (
    <Sheet open={!!opportunityId} onOpenChange={onOpenChange}>
      <SheetContent className="w-[700px] sm:max-w-[700px] overflow-y-auto p-0" aria-describedby={undefined}>
        <VisuallyHidden.Root>
          <SheetTitle>Détail de l'opportunité</SheetTitle>
        </VisuallyHidden.Root>
        {loading ? (
          <div className="space-y-4 p-6">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : data ? (
          <div className="p-6">
            <OpportunityShow
              opportunity={data.opportunity}
              donations={data.donations}
              recurring_donations={data.recurring_donations}
            />
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
